import type { UsageSummary } from '@/lib/api'
import { useLocale, useT } from '@/i18n/provider'
import { formatCompactNumber } from '@/lib/format'

interface UsageChartProps {
  data: UsageSummary
}

export function UsageChart({ data }: UsageChartProps) {
  const t = useT()
  const { locale } = useLocale()
  const { timeline } = data
  if (!timeline || timeline.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-white/30 text-sm">
        {t('usage.noUsage')}
      </div>
    )
  }

  const maxTokens = Math.max(...timeline.map((d) => d.tokens), 1)

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-1 h-28">
        {timeline.map((day) => {
          const height = Math.max((day.tokens / maxTokens) * 100, 4)
          return (
            <div key={day.date} className="flex-1 flex flex-col items-center gap-1 group relative">
              {/* Tooltip */}
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 glass-sm px-2 py-1 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                <p className="text-white/80">{formatCompactNumber(day.tokens, locale)} {t('common.tokens')}</p>
                <p className="text-white/40">{day.date}</p>
              </div>
              <div
                className="w-full rounded-sm bg-[var(--oc-accent)]/50 hover:bg-[var(--oc-accent)]/80 transition-colors"
                style={{ height: `${height}%` }}
              />
            </div>
          )
        })}
      </div>
      <div className="flex justify-between text-xs text-white/30">
        <span>{timeline[0]?.date}</span>
        <span>{timeline[timeline.length - 1]?.date}</span>
      </div>
    </div>
  )
}
